import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import c from 'classnames';

import { CoreLink } from '@uncinc/react-drupal-core';
import { Container, NavigationScroll } from '@uncinc/uncinc-react-kitchen-sink';

import {
  MenuMain,
  MenuMainOverlay,
  MenuSecondary,
} from '../../implementations';
import { ReactComponent as LogoSVG } from '../../assets/images/rgb-jubileum-logo.svg';
import { APP_OPTIONS } from '../../config/constants';

import './style.scss';

const Header = () => {
  const { t } = useTranslation();
  const searchRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [searchVisible, setSearchVisible] = useState(false);
  const [overlayMenuVisible, setOverlayMenuVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const history = useHistory();
  const currentLanguage = useSelector((state) => state.getIn(['ui', 'language']));

  useEffect(() => {
    if (searchRef.current && searchVisible) {
      searchRef.current.focus();
    }
  }, [searchRef, searchVisible]);

  // Prevent overlay from being displayed initially.
  window.addEventListener('load', () => {
    setLoaded(true);
  });

  useEffect(() => {
    document.body.classList.toggle('Header__overlay--body', overlayMenuVisible);
  }, [overlayMenuVisible]);

  const classes = c({
    expanded: overlayMenuVisible,
    preload: !loaded,
  });

  const submitSearch = () => {
    if (overlayMenuVisible) setOverlayMenuVisible(false);
    if (searchVisible) setSearchVisible(false);

    const pathname = t('paths.search');
    let search = '';

    if (inputValue) {
      search = `?${APP_OPTIONS.parameters.searchkitIndexAllContent}[query]=${inputValue}`;
    }

    history.push({ pathname, search });
  };

  useEffect(() => {
    const escFunction = ({ key }) => {
      if (key === 'Escape') {
        setSearchVisible(false);
      }
    };

    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, []);

  return (
    <NavigationScroll>
      <header className={classes}>
        <Container className="Header__container">
          <div className="row">

            <div className={`Header__logo ${searchVisible ? 'mb-hidden' : ''} col-2`}>
              <CoreLink
                to={currentLanguage === 'nl' ? '/' : `/${currentLanguage}`}
                onClick={() => setOverlayMenuVisible(false)}
              >
                <div className="Header__logo__img" alt="De Regenboog Groep logo">
                  <LogoSVG />
                </div>
              </CoreLink>
            </div>

            <div className="Header col-10">
              <div className="Header__menu">
                <div className="Header__menu__primary">
                  <MenuMain setOverlayMenuVisible={setOverlayMenuVisible} />
                </div>
                <div className="Header__menu__secondary">
                  <MenuSecondary setOverlayMenuVisible={setOverlayMenuVisible} id="main-cta" />
                </div>
                <div className={`Header__menu__secondary__search ${searchVisible ? 'visible' : ''}`}>
                  <input
                    onChange={({ target: { value } }) => {
                      setInputValue(value);
                    }}
                    onKeyDown={({ key }) => {
                      if (key === 'Enter' && inputValue) {
                        submitSearch();
                      }
                    }}
                    type="text"
                    name="search"
                    id="search"
                    placeholder={t('menu.search_placeholder')}
                    ref={searchRef}
                  />

                  <div>
                    <div>
                      <span
                        className="icon icon-search icon-variant-1"
                        onClick={submitSearch}
                      />
                    </div>
                    <div>
                      <span
                        className="icon icon-cross"
                        onClick={() => {
                          setSearchVisible(false);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="Header__buttons">
                <i
                  onClick={() => {
                    // If we are on the search page redirect the focus to the search page search bar.
                    if (window.location.pathname === t('paths.search')) {
                      const input = document.querySelectorAll('input[name="query"]')[0];
                      if (input) {
                        input.focus();
                        return;
                      }
                    }

                    setSearchVisible(true);
                  }}
                  className="icon icon-search" />
                <i
                  onClick={() => {
                    setOverlayMenuVisible(!overlayMenuVisible);
                  }}
                  className={`icon icon-${overlayMenuVisible ? 'cross' : 'hamburger'}`} />
              </div>
            </div>
          </div>
        </Container>
        <div className="Header__overlay">
          <Container>
            <div className="row">
              <div className="col-12 Header__overlay__main_menu order-2 order-lg-1">
                <MenuMainOverlay
                  onClick={() => setOverlayMenuVisible((prevMenuActive) => !prevMenuActive)}
                  setOverlayMenuVisible={setOverlayMenuVisible}
                  when={overlayMenuVisible}
                />
              </div>
            </div>
          </Container>
        </div>
      </header>
    </NavigationScroll>
  );
};

export default Header;
